import React, { useEffect, useState, useCallback } from "react";
import { confirm } from "../utils/confirm";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { messaggioErrore } from "../utils/erroriUtente";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { Plus, X, Zap, ChevronDown, ChevronRight, ToggleLeft, ToggleRight } from "lucide-react";

// ─── DEFINIZIONI TRIGGER / CONDIZIONI / AZIONI ───────────────────────────
export const TRIGGERS = [
  { id: "task_created",        label: "Attività creata" },
  { id: "task_completed",      label: "Attività completata" },
  { id: "task_due_today",      label: "Attività in scadenza oggi" },
  { id: "task_overdue",        label: "Attività scaduta" },
  { id: "task_assigned",       label: "Attività assegnata a qualcuno" },
  { id: "task_status_changed", label: "Stato attività cambiato" },
  { id: "task_priority_changed",label: "Priorità attività cambiata" },
  { id: "subtask_completed",   label: "Tutte le sottoattività completate" },
];

export const CONDITION_FIELDS = [
  { id: "priority",    label: "Priorità",    type: "select", options: ["Alta","Media","Bassa"] },
  { id: "status",      label: "Stato",       type: "select", options: ["todo","in_progress","done","blocked"] },
  { id: "assignee",    label: "Assegnato a", type: "user" },
  { id: "section",     label: "Sezione",     type: "text" },
  { id: "tag",         label: "Tag contiene",type: "text" },
];

export const CONDITION_OPS = [
  { id: "eq",  label: "è uguale a" },
  { id: "neq", label: "è diverso da" },
  { id: "contains", label: "contiene" },
];

export const ACTIONS = [
  { id: "set_status",    label: "Cambia stato",        param: "select", options: ["todo","in_progress","done","blocked"] },
  { id: "set_priority",  label: "Cambia priorità",     param: "select", options: ["Alta","Media","Bassa"] },
  { id: "assign_to",     label: "Assegna a utente",    param: "user" },
  { id: "add_tag",       label: "Aggiungi tag",         param: "text" },
  { id: "add_comment",   label: "Aggiungi commento",    param: "text" },
  { id: "notify",        label: "Invia notifica a",     param: "user" },
  { id: "move_section",  label: "Sposta in sezione",    param: "text" },
];

// ─── MODAL CREA/MODIFICA REGOLA ──────────────────────────────────────────
function RegolaModal({ regola, onClose, orgId, projectId, utenti, sezioni }) {
  const { addToast } = useToast();
  const [nome, setNome] = useState(regola?.nome || "");
  const [trigger, setTrigger] = useState(regola?.trigger || "");
  const [condizioni, setCondizioni] = useState(regola?.condizioni || []);
  const [azioni, setAzioni] = useState(regola?.azioni || [{ id: "", param: "" }]);
  const [saving, setSaving] = useState(false);

  const aggiungiCondizione = () => setCondizioni(c => [...c, { field: "", op: "eq", value: "" }]);
  const rimuoviCondizione = i => setCondizioni(c => c.filter((_, idx) => idx !== i));
  const updateCondizione = (i, field, val) => setCondizioni(c => c.map((x, idx) => idx === i ? { ...x, [field]: val } : x));

  const aggiungiAzione = () => setAzioni(a => [...a, { id: "", param: "" }]);
  const rimuoviAzione = i => setAzioni(a => a.filter((_, idx) => idx !== i));
  const updateAzione = (i, field, val) => setAzioni(a => a.map((x, idx) => idx === i ? { ...x, [field]: val } : x));

  const salva = async () => {
    if (!nome.trim()) { addToast("Nome regola obbligatorio", "error"); return; }
    if (!trigger) { addToast("Seleziona un trigger", "error"); return; }
    if (azioni.some(a => !a.id)) { addToast("Completa tutte le azioni", "error"); return; }
    setSaving(true);
    try {
      const dati = {
        nome: nome.trim(), trigger, condizioni, azioni,
        orgId, projectId: projectId || null,
        attiva: regola?.attiva !== false,
        scope: projectId ? "progetto" : "globale",
        updatedAt: serverTimestamp(),
      };
      if (regola?.id) {
        await updateDoc(doc(db, "regole", regola.id), dati);
      } else {
        await addDoc(collection(db, "regole"), { ...dati, createdAt: serverTimestamp() });
      }
      addToast("Regola salvata ✅", "success");
      onClose();
    } catch (err) { addToast(messaggioErrore(err), "error"); }
    setSaving(false);
  };

  const inputStyle = { width: "100%", padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 13, fontFamily: "'Inter',sans-serif", outline: "none", boxSizing: "border-box" };
  const selectStyle = { ...inputStyle };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
            <Zap size={18} color="#7c3aed" /> {regola ? "Modifica regola" : "Nuova regola"}
          </h3>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Nome */}
        <div className="form-group">
          <label className="form-label">Nome regola *</label>
          <input style={inputStyle} value={nome} onChange={e => setNome(e.target.value)} placeholder="Es. Notifica scadenza alta priorità" />
        </div>

        {/* Trigger */}
        <div className="form-group">
          <label className="form-label">Quando (Trigger) *</label>
          <select style={selectStyle} value={trigger} onChange={e => setTrigger(e.target.value)}>
            <option value="">— Seleziona trigger —</option>
            {TRIGGERS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>

        {/* Condizioni */}
        <div className="form-group">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <label className="form-label" style={{ marginBottom: 0 }}>Condizioni (opzionali)</label>
            <button onClick={aggiungiCondizione}
              style={{ fontSize: 11, color: "#7c3aed", background: "none", border: "1px solid #7c3aed", borderRadius: 6, padding: "2px 8px", cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              + Aggiungi
            </button>
          </div>
          {condizioni.length === 0 && <div style={{ fontSize: 12, color: "#9ca3af" }}>Nessuna condizione — la regola si applica sempre</div>}
          {condizioni.map((cond, i) => {
            const fieldDef = CONDITION_FIELDS.find(f => f.id === cond.field);
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 6, marginBottom: 6, alignItems: "center" }}>
                <select style={{ ...selectStyle, fontSize: 11 }} value={cond.field} onChange={e => updateCondizione(i, "field", e.target.value)}>
                  <option value="">— Campo —</option>
                  {CONDITION_FIELDS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
                <select style={{ ...selectStyle, fontSize: 11 }} value={cond.op} onChange={e => updateCondizione(i, "op", e.target.value)}>
                  {CONDITION_OPS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
                {fieldDef?.type === "select" ? (
                  <select style={{ ...selectStyle, fontSize: 11 }} value={cond.value} onChange={e => updateCondizione(i, "value", e.target.value)}>
                    <option value="">— Valore —</option>
                    {fieldDef.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : fieldDef?.type === "user" ? (
                  <select style={{ ...selectStyle, fontSize: 11 }} value={cond.value} onChange={e => updateCondizione(i, "value", e.target.value)}>
                    <option value="">— Utente —</option>
                    {utenti.map(u => <option key={u.uid} value={u.uid}>{u.displayName}</option>)}
                  </select>
                ) : (
                  <input style={{ ...inputStyle, fontSize: 11 }} value={cond.value} onChange={e => updateCondizione(i, "value", e.target.value)} placeholder="Valore..." />
                )}
                <button onClick={() => rimuoviCondizione(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#d1d5db" }}
                  onMouseOver={e => e.currentTarget.style.color = "#dc2626"} onMouseOut={e => e.currentTarget.style.color = "#d1d5db"}>
                  <X size={13} />
                </button>
              </div>
            );
          })}
        </div>

        {/* Azioni */}
        <div className="form-group">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <label className="form-label" style={{ marginBottom: 0 }}>Allora (Azioni) *</label>
            <button onClick={aggiungiAzione}
              style={{ fontSize: 11, color: "#7c3aed", background: "none", border: "1px solid #7c3aed", borderRadius: 6, padding: "2px 8px", cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              + Aggiungi
            </button>
          </div>
          {azioni.map((azione, i) => {
            const azioneDef = ACTIONS.find(a => a.id === azione.id);
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 6, marginBottom: 6, alignItems: "center" }}>
                <select style={{ ...selectStyle, fontSize: 11 }} value={azione.id} onChange={e => updateAzione(i, "id", e.target.value)}>
                  <option value="">— Seleziona azione —</option>
                  {ACTIONS.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
                {azioneDef?.param === "select" ? (
                  <select style={{ ...selectStyle, fontSize: 11 }} value={azione.param} onChange={e => updateAzione(i, "param", e.target.value)}>
                    <option value="">— Valore —</option>
                    {azioneDef.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : azioneDef?.param === "user" ? (
                  <select style={{ ...selectStyle, fontSize: 11 }} value={azione.param} onChange={e => updateAzione(i, "param", e.target.value)}>
                    <option value="">— Utente —</option>
                    {utenti.map(u => <option key={u.uid} value={u.uid}>{u.displayName}</option>)}
                  </select>
                ) : azioneDef?.param === "text" ? (
                  <input style={{ ...inputStyle, fontSize: 11 }} value={azione.param} onChange={e => updateAzione(i, "param", e.target.value)} placeholder="Valore..." />
                ) : <div />}
                <button onClick={() => azioni.length > 1 && rimuoviAzione(i)} style={{ background: "none", border: "none", cursor: azioni.length > 1 ? "pointer" : "not-allowed", color: "#d1d5db" }}
                  onMouseOver={e => { if (azioni.length > 1) e.currentTarget.style.color = "#dc2626"; }} onMouseOut={e => e.currentTarget.style.color = "#d1d5db"}>
                  <X size={13} />
                </button>
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" onClick={salva} disabled={saving}>{saving ? "Salvo..." : "Salva regola"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── CARD REGOLA ─────────────────────────────────────────────────────────
function RegolaCard({ regola, onEdit, onDelete, onToggle }) {
  const trigger = TRIGGERS.find(t => t.id === regola.trigger);
  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 16, borderLeft: `4px solid ${regola.attiva ? "#7c3aed" : "#d1d5db"}` }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <Zap size={14} color={regola.attiva ? "#7c3aed" : "#d1d5db"} />
            <span style={{ fontSize: 14, fontWeight: 700, color: regola.attiva ? "#111827" : "#9ca3af" }}>{regola.nome}</span>
            <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 8, background: regola.scope === "globale" ? "#ede9fe" : "#dbeafe", color: regola.scope === "globale" ? "#7c3aed" : "#2563eb" }}>
              {regola.scope === "globale" ? "Globale" : "Commessa"}
            </span>
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
            <span style={{ fontWeight: 600 }}>Trigger:</span> {trigger?.label || regola.trigger}
          </div>
          {regola.condizioni?.length > 0 && (
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
              <span style={{ fontWeight: 600 }}>Condizioni:</span> {regola.condizioni.length} condizione/i
            </div>
          )}
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            <span style={{ fontWeight: 600 }}>Azioni:</span> {regola.azioni?.map(a => ACTIONS.find(x => x.id === a.id)?.label || a.id).join(", ")}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
          <button onClick={onToggle} title={regola.attiva ? "Disattiva" : "Attiva"}
            style={{ background: "none", border: "none", cursor: "pointer", color: regola.attiva ? "#7c3aed" : "#d1d5db", padding: 2 }}>
            {regola.attiva ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
          </button>
          <button onClick={onEdit}
            style={{ fontSize: 11, padding: "4px 10px", border: "1px solid #e5e7eb", borderRadius: 7, background: "#fff", cursor: "pointer", fontFamily: "'Inter',sans-serif", color: "#6b7280" }}>
            Modifica
          </button>
          <button onClick={onDelete}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#d1d5db", padding: 2 }}
            onMouseOver={e => e.currentTarget.style.color = "#dc2626"} onMouseOut={e => e.currentTarget.style.color = "#d1d5db"}>
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PAGINA PRINCIPALE ───────────────────────────────────────────────────
export default function RegolePage() {
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const [regole, setRegole] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRegola, setEditingRegola] = useState(null);
  const [activeTab, setActiveTab] = useState("globali");

  const canEdit = isOrgAdmin || isPlatformOwner;

  useEffect(() => {
    if (!effectiveOrgId) return;
    const q = query(collection(db, "regole"), where("orgId", "==", effectiveOrgId));
    return onSnapshot(q, snap => setRegole(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [effectiveOrgId]);

  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(query(collection(db, "users"), where("organizationId", "==", effectiveOrgId), where("enabled", "==", true)),
      snap => setUtenti(snap.docs.map(d => d.data())));
  }, [effectiveOrgId]);

  const elimina = async id => {
    if (!await confirm("Eliminare questa regola?", { type:"danger" })) return;
    await deleteDoc(doc(db, "regole", id));
    addToast("Regola eliminata", "info");
  };

  const toggle = async regola => {
    await updateDoc(doc(db, "regole", regola.id), { attiva: !regola.attiva });
    addToast(regola.attiva ? "Regola disattivata" : "Regola attivata", "success");
  };

  const regoleGlobali = regole.filter(r => r.scope === "globale");
  const regoleCommessa = regole.filter(r => r.scope === "progetto");

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "14px 24px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
            <Zap size={18} color="#7c3aed" /> Regole & Automazioni
          </h2>
          {canEdit && (
            <button onClick={() => { setEditingRegola(null); setShowModal(true); }} className="btn btn-primary" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Plus size={14} /> Nuova regola
            </button>
          )}
        </div>
        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
          Le regole si attivano automaticamente quando si verificano gli eventi specificati sulle attività.
        </div>
      </div>

      {/* Tab */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "0 24px", flexShrink: 0, display: "flex", gap: 2 }}>
        {[
          { id: "globali", label: `🌐 Globali (${regoleGlobali.length})` },
          { id: "commessa", label: `📋 Per commessa (${regoleCommessa.length})` },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: "8px 16px", border: "none", borderBottom: activeTab === t.id ? "2px solid #7c3aed" : "2px solid transparent", background: "none", color: activeTab === t.id ? "#7c3aed" : "#6b7280", fontWeight: activeTab === t.id ? 700 : 400, cursor: "pointer", fontFamily: "'Inter',sans-serif", fontSize: 13 }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Lista regole */}
      <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
        {activeTab === "globali" && (
          <>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>
              Le regole globali si applicano a <strong>tutte le commesse</strong> dell'organizzazione. Solo gli amministratori possono crearle.
            </div>
            {regoleGlobali.length === 0 ? (
              <div style={{ textAlign: "center", padding: 48, color: "#9ca3af" }}>
                <Zap size={40} style={{ opacity: 0.2, marginBottom: 12 }} />
                <div style={{ fontSize: 14, fontWeight: 600 }}>Nessuna regola globale</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>Crea una regola che si applica a tutte le commesse</div>
                {canEdit && <button onClick={() => { setEditingRegola(null); setShowModal(true); }} className="btn btn-primary" style={{ marginTop: 16 }}>+ Crea regola</button>}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {regoleGlobali.map(r => (
                  <RegolaCard key={r.id} regola={r}
                    onEdit={() => { setEditingRegola(r); setShowModal(true); }}
                    onDelete={() => elimina(r.id)}
                    onToggle={() => toggle(r)} />
                ))}
              </div>
            )}
          </>
        )}
        {activeTab === "commessa" && (
          <>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>
              Le regole per commessa si applicano solo alla commessa specifica. I PM possono crearle dalla tab <strong>Impostazioni</strong> della commessa.
            </div>
            {regoleCommessa.length === 0 ? (
              <div style={{ textAlign: "center", padding: 48, color: "#9ca3af" }}>
                <Zap size={40} style={{ opacity: 0.2, marginBottom: 12 }} />
                <div style={{ fontSize: 14, fontWeight: 600 }}>Nessuna regola per commessa</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>Le regole create dai PM nelle singole commesse appariranno qui</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {regoleCommessa.map(r => (
                  <RegolaCard key={r.id} regola={r}
                    onEdit={() => { setEditingRegola(r); setShowModal(true); }}
                    onDelete={() => elimina(r.id)}
                    onToggle={() => toggle(r)} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {showModal && (
        <RegolaModal
          regola={editingRegola}
          orgId={effectiveOrgId}
          projectId={null}
          utenti={utenti}
          sezioni={[]}
          onClose={() => { setShowModal(false); setEditingRegola(null); }}
        />
      )}
    </div>
  );
}
